import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Rocket, ArrowRight, Check, Upload, Brain, CreditCard, Calendar,
  FileText, Zap, Shield, Clock, ChevronDown, Mail,
  Building2, Star, Sparkles, LayoutGrid, CheckCircle, Flame,
  Wallet, Users, Download, Send, Phone, MapPin, Bot, SortAsc,
  BadgeCheck, Scan, MousePointerClick, FolderCheck, MessageSquareText,
  FileSearch, Share2, Receipt, Timer, Inbox, Play, Eye, Edit3,
  Trash2, X, MoreHorizontal, AlertCircle, Sun, FolderOpen, Flag,
  ShieldAlert, Swords
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

// Animated Counter Component for Dashboard Stats
const AnimatedAmount = () => {
  const amounts = ['499 €', '624 €', '812 €', '1.247 €', '1.589 €', '2.134 €', '2.847 €'];
  const [idx, setIdx] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setIdx(prev => (prev + 1) % amounts.length);
    }, 1200);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <motion.span
      key={idx}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.3 }}
    >
      {amounts[idx]}
    </motion.span>
  );
};

// Synchronized Dashboard Animation - Matches Real Dashboard Design
const DashboardCockpit = () => {
  const [uploadCycle, setUploadCycle] = useState(0);
  const [showPdf, setShowPdf] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  
  // Data that changes with each upload - only +1 increments
  const cycleData = [
    { amount: '499 €', dringend: 4, pipeline: 8, erledigt: 22, doc: { name: 'Rechnung Telekom', amount: '49,99 €', type: 'Rechnung' }, task: 'Telekom bezahlen' },
    { amount: '549 €', dringend: 5, pipeline: 9, erledigt: 23, doc: { name: 'Rechnung Vodafone', amount: '49,99 €', type: 'Rechnung' }, task: 'Vodafone prüfen' },
    { amount: '599 €', dringend: 6, pipeline: 10, erledigt: 24, doc: { name: 'Stromrechnung E.ON', amount: '49,99 €', type: 'Rechnung' }, task: 'Strom bezahlen' },
    { amount: '649 €', dringend: 7, pipeline: 11, erledigt: 25, doc: { name: 'Versicherung HUK', amount: '49,99 €', type: 'Rechnung' }, task: 'HUK anrufen' },
    { amount: '699 €', dringend: 8, pipeline: 12, erledigt: 26, doc: { name: 'Postbank Kontoauszug', amount: '49,99 €', type: 'Rechnung' }, task: 'Kontoauszug ablegen' },
  ];
  
  const currentData = cycleData[uploadCycle];
  
  useEffect(() => {
    const cycleAnimation = () => {
      setShowPdf(true);
      setTimeout(() => {
        setShowPdf(false);
        setShowNotification(true);
      }, 2000);
      setTimeout(() => {
        setUploadCycle(prev => (prev + 1) % cycleData.length);
      }, 3000);
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    };
    
    cycleAnimation();
    const interval = setInterval(cycleAnimation, 6000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="bg-gradient-to-br from-[#0f0a1e] via-[#1a1030] to-[#0d0719] rounded-3xl shadow-[0_0_60px_rgba(139,92,246,0.3)] overflow-hidden border border-violet-500/20">
      {/* Browser Chrome */}
      <div className="px-4 py-3 bg-black/60 flex items-center gap-3 border-b border-violet-500/20">
        <div className="flex gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <div className="flex-1 flex justify-center">
          <div className="bg-violet-500/20 rounded-lg px-4 py-1.5 text-sm text-violet-200 font-medium">app.jaysmart.de</div>
        </div>
      </div>
      
      {/* Dashboard Content - Matches Real Dashboard */}
      <div className="p-6 lg:p-8 relative overflow-hidden min-h-[500px]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.25),transparent_60%)]"></div>
        
        {/* Notification Toast */}
        <AnimatePresence>
          {showNotification && (
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              className="absolute top-4 right-4 z-50 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl px-4 py-3 shadow-xl shadow-emerald-500/30 flex items-center gap-3"
            >
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Erfolgreich hochgeladen!</p>
                <p className="text-white/80 text-xs">{currentData.doc.name}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        <div className="relative z-10">
          {/* Top Bar - Like Real Dashboard */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-violet-300/80 text-sm">Guten Tag,</p>
              <h3 className="text-2xl font-bold text-white">Jay Adel</h3>
              <p className="text-white/40 text-xs mt-1">Montag, 2. März 2026</p>
            </div>
            
            {/* Weather Widget - Matches Real Dashboard */}
            <div className="bg-white/5 backdrop-blur rounded-xl px-4 py-2.5 flex items-center gap-3 border border-white/10">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/25">
                <Sun className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-white">18°</p>
                <p className="text-xs text-white/50">Berlin</p>
              </div>
            </div>
          </div>
          
          {/* Stats Cards - Exact Match to Real Dashboard */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-2xl p-4 shadow-xl shadow-violet-500/30">
              <div className="w-8 h-8 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center mb-2">
                <Wallet className="w-4 h-4 text-white" />
              </div>
              <motion.div className="text-xl font-bold text-white" key={currentData.amount} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {currentData.amount}
              </motion.div>
              <p className="text-xs text-white/70">Offene Summe</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/25 mb-2">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <motion.p className="text-xl font-bold text-orange-400" key={currentData.dringend} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {currentData.dringend}
              </motion.p>
              <p className="text-xs text-white/50">Dringend</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25 mb-2">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <motion.p className="text-xl font-bold text-blue-400" key={currentData.pipeline} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {currentData.pipeline}
              </motion.p>
              <p className="text-xs text-white/50">In der Pipeline</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25 mb-2">
                <Check className="w-4 h-4 text-white" />
              </div>
              <motion.p className="text-xl font-bold text-emerald-400" key={currentData.erledigt} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {currentData.erledigt}
              </motion.p>
              <p className="text-xs text-white/50">Erledigt</p>
            </div>
          </div>
          
          {/* Main Content Grid - Matches Real Dashboard Layout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Upload Zone - Exact Match */}
            <motion.div 
              className="md:col-span-5 bg-white/5 backdrop-blur rounded-2xl p-6 border-2 border-dashed border-violet-500/30 relative overflow-hidden"
              animate={{ 
                borderColor: showPdf ? ['rgba(139,92,246,0.3)', 'rgba(139,92,246,1)', 'rgba(139,92,246,0.3)'] : 'rgba(139,92,246,0.3)',
                boxShadow: showPdf ? ['0 0 0 rgba(139,92,246,0)', '0 0 40px rgba(139,92,246,0.6)', '0 0 0 rgba(139,92,246,0)'] : '0 0 0 rgba(139,92,246,0)'
              }}
              transition={{ duration: 1.5 }}
            >
              <AnimatePresence>
                {showPdf && (
                  <motion.div
                    className="absolute z-20"
                    initial={{ x: -80, y: -60, opacity: 1, rotate: -15 }}
                    animate={{ x: 60, y: 60, opacity: 1, rotate: 0 }}
                    exit={{ y: 80, opacity: 0, scale: 0.8 }}
                    transition={{ duration: 1.8, ease: "easeOut" }}
                  >
                    <div className="w-12 h-14 bg-gradient-to-br from-red-500 to-rose-600 rounded-lg shadow-xl shadow-red-500/50 flex flex-col items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                      <span className="text-[8px] text-white font-bold mt-0.5">PDF</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex flex-col items-center text-center relative z-10">
                <motion.div 
                  className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center mb-3"
                  animate={{ scale: showPdf ? [1, 1.2, 1] : 1 }}
                  transition={{ duration: 1.5 }}
                >
                  <Upload className="w-7 h-7 text-violet-400" />
                </motion.div>
                <p className="font-semibold text-white text-sm mb-1">Dokument hochladen</p>
                <p className="text-xs text-white/40 mb-3">PDF, JPG oder PNG per Drag & Drop</p>
                <button className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-xs text-white transition-colors">
                  Datei auswählen
                </button>
              </div>
            </motion.div>
            
            {/* Frist-Wächter Section - With Sword Icon and Days Counter like Real Dashboard */}
            <div className="md:col-span-7 bg-white/5 backdrop-blur rounded-2xl overflow-hidden border border-white/10">
              <div className="px-4 py-3 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <ShieldAlert className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="font-bold text-white text-sm flex items-center gap-2">
                    Frist-Wächter
                    <Swords className="w-4 h-4 text-white/80" />
                  </span>
                  <p className="text-white/70 text-xs">3 Dokumente mit Fristen</p>
                </div>
              </div>
              <div className="p-4 space-y-2">
                {/* First document with days badge */}
                <motion.div 
                  className="flex items-center gap-3 p-3 bg-red-500/10 rounded-xl border border-red-500/30"
                  key={`doc1-${uploadCycle}`}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                >
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg leading-none">2</span>
                    <span className="text-white/80 text-[9px] font-medium">TAGE</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm truncate">{currentData.doc.name}</p>
                    <p className="text-xs text-red-400">Noch 2 Tage · 16. März</p>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-white text-sm">{currentData.doc.amount}</span>
                    <p className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded inline-block">Rechnung</p>
                  </div>
                </motion.div>
                {/* Second document */}
                <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-xl border border-amber-500/30">
                  <div className="w-12 h-12 bg-amber-500 rounded-xl flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg leading-none">7</span>
                    <span className="text-white/80 text-[9px] font-medium">TAGE</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm truncate">Göller, Methmann & Hansen</p>
                    <p className="text-xs text-amber-400">Noch 7 Tage · 3. März</p>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-white text-sm">373,51 €</span>
                    <p className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded inline-block">Sonstiges</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* KI Analysiert Badge */}
          <motion.div 
            className="absolute bottom-4 right-4 bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl px-4 py-2.5 shadow-lg shadow-violet-500/30"
            animate={{ scale: showNotification ? [1, 1.05, 1] : 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-white" />
              <div>
                <p className="text-white font-semibold text-xs">KI analysiert</p>
                <p className="text-white/70 text-[10px]">{currentData.pipeline} neue Dokumente</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isYearlyPricing, setIsYearlyPricing] = useState(false);

  const features = [
    { icon: Scan, title: 'KI-Erkennung', description: 'Absender, Beträge, IBAN automatisch erkannt', gradient: 'from-blue-500 to-cyan-400' },
    { icon: LayoutGrid, title: 'Kanban Board', description: 'Dokumente visuell verwalten', gradient: 'from-violet-500 to-purple-400' },
    { icon: Timer, title: 'Fristen-Wächter', description: 'Nie wieder Fristen verpassen', gradient: 'from-orange-500 to-red-400' },
    { icon: MessageSquareText, title: 'KI-Antworten', description: 'E-Mails per Klick generieren', gradient: 'from-pink-500 to-rose-400' },
    { icon: CreditCard, title: 'One-Click Pay', description: 'Rechnungen sofort bezahlen', gradient: 'from-emerald-500 to-teal-400' },
    { icon: Share2, title: 'Steuerberater-Export', description: 'Alle Belege als ZIP', gradient: 'from-indigo-500 to-blue-400' }
  ];

  const faqs = [
    { question: 'Wie funktioniert die KI-Erkennung?', answer: 'Unsere KI analysiert jedes hochgeladene Dokument automatisch. Sie erkennt den Absender, extrahiert den Betrag, die Frist, IBAN und Verwendungszweck.' },
    { question: 'Ist JaySmart auch für Privatpersonen geeignet?', answer: 'Ja! JaySmart unterscheidet automatisch zwischen privaten und geschäftlichen Dokumenten.' },
    { question: 'Wie sicher sind meine Daten?', answer: 'Deine Daten werden verschlüsselt auf deutschen Servern gespeichert. Wir sind DSGVO-konform.' },
    { question: 'Kann ich JaySmart mit meinem Steuerberater teilen?', answer: 'Ja! Du kannst alle Dokumente als ZIP exportieren oder deinen Steuerberater direkt einladen.' }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>JaySmart</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <a href="#prinzip" className="text-gray-600 hover:text-violet-600 transition-colors font-medium">Das Prinzip</a>
              <a href="#features" className="text-gray-600 hover:text-violet-600 transition-colors font-medium">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-violet-600 transition-colors font-medium">Preise</a>
              <a href="#faq" className="text-gray-600 hover:text-violet-600 transition-colors font-medium">FAQ</a>
              <a href="#kontakt" className="text-gray-600 hover:text-violet-600 transition-colors font-medium">Kontakt</a>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/login">
                <Button variant="ghost" className="rounded-full font-semibold text-sm sm:text-base px-3 sm:px-4 hidden sm:inline-flex text-gray-700 hover:text-violet-600 hover:bg-violet-50">Anmelden</Button>
              </Link>
              <Link to="/signup">
                <Button className="rounded-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 font-semibold text-xs sm:text-sm px-3 sm:px-4">
                  Kostenlos testen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* ========== 1. HERO - Problem & Lösung ========== */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-violet-50/50 via-white to-white relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] sm:w-[500px] md:w-[700px] lg:w-[800px] h-[300px] sm:h-[500px] md:h-[700px] lg:h-[800px] bg-gradient-to-br from-violet-200/30 to-purple-200/20 rounded-full blur-3xl -z-10 pointer-events-none"></div>
        
        <div className="max-w-5xl mx-auto px-6 text-center">
          {/* Badge */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold">
              <Sparkles className="w-4 h-4" />
              Für Selbstständige & kleine Unternehmen
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-[1.1]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Schluss mit dem
            <br />
            <span className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
              Papierchaos.
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            Briefe hochladen, KI erkennt alles automatisch, Fristen im Blick – 
            <span className="text-gray-900 font-medium"> nie wieder eine Rechnung oder Mahnung verpassen.</span>
          </motion.p>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
          >
            <Link to="/signup">
              <Button size="lg" className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-full px-10 h-14 text-lg font-semibold shadow-xl shadow-violet-500/25 group">
                14 Tage kostenlos testen
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href="#demo">
              <Button size="lg" className="bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-600 hover:to-cyan-600 text-white rounded-full px-10 h-14 text-lg font-semibold shadow-xl shadow-indigo-500/25">
                <Play className="w-5 h-5 mr-2" />
                Demo ansehen
              </Button>
            </a>
          </motion.div>

          {/* Trust - Updated Badges */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-sm text-gray-500 flex items-center justify-center gap-6 flex-wrap"
          >
            <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-emerald-500" /> Daten lokal gespeichert</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> 14 Tage kostenlos</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Jederzeit kündbar</span>
          </motion.p>
        </div>
      </section>

      {/* ========== 2. DASHBOARD COCKPIT PREVIEW ========== */}
      <section className="py-20 bg-gradient-to-b from-white via-slate-50 to-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full text-sm font-semibold mb-4 shadow-lg shadow-violet-500/30">
              Dein Cockpit
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Alles auf einen Blick
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Dein persönliches Dashboard mit allen wichtigen Infos.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* New synchronized Dashboard component */}
            <DashboardCockpit />
          </motion.div>
        </div>
      </section>

      {/* ========== 4. DAS 1-2-3 PRINZIP ========== */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
              So einfach geht's
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Das 1-2-3 Prinzip
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-xl shadow-violet-500/30 relative">
                <Upload className="w-10 h-10 text-white" />
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-violet-500">
                  <span className="text-violet-600 font-bold">1</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Rechnung hochladen</h3>
              <p className="text-gray-600">Foto machen oder PDF hochladen – fertig.</p>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-3xl flex items-center justify-center shadow-xl shadow-indigo-500/30 relative">
                <Brain className="w-10 h-10 text-white" />
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-indigo-500">
                  <span className="text-indigo-600 font-bold">2</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">KI erkennt alles</h3>
              <p className="text-gray-600">Absender, Betrag, Frist, IBAN – automatisch.</p>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-3xl flex items-center justify-center shadow-xl shadow-emerald-500/30 relative">
                <CreditCard className="w-10 h-10 text-white" />
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-emerald-500">
                  <span className="text-emerald-600 font-bold">3</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Bezahlen</h3>
              <p className="text-gray-600">Ein Klick – Überweisung erledigt.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== 5. PRICING ========== */}
      <section id="pricing" className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
              Preise
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Investiere in deine Zeit
            </h2>
            <p className="text-lg text-gray-600 mb-8">Spare Stunden pro Woche – für einen Bruchteil deines Stundensatzes.</p>
            
            {/* Monthly/Yearly Toggle */}
            <div className="flex justify-center">
              <div className="bg-gray-200 rounded-full p-1 flex">
                <button
                  onClick={() => setIsYearlyPricing(false)}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${!isYearlyPricing ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Monatlich
                </button>
                <button
                  onClick={() => setIsYearlyPricing(true)}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${isYearlyPricing ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Jährlich
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full">-17%</span>
                </button>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Starter - Entry Level */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Starter</h3>
              <p className="text-gray-500 text-sm mb-6">Ideal für den Einstieg</p>
              <div className="mb-2">
                <span className="text-4xl font-bold text-gray-900">{isYearlyPricing ? '99' : '9,90'} €</span>
                <span className="text-gray-500">/{isYearlyPricing ? 'Jahr' : 'Monat'}</span>
              </div>
              {isYearlyPricing && <p className="text-emerald-600 text-xs mb-4">Spare 19,80 € pro Jahr!</p>}
              {!isYearlyPricing && <p className="text-gray-400 text-xs mb-4">&nbsp;</p>}
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-gray-600 text-sm"><Check className="w-4 h-4 text-emerald-500" />50 Dokumente/Monat</li>
                <li className="flex items-center gap-2 text-gray-600 text-sm"><Check className="w-4 h-4 text-emerald-500" />KI-Dokumentenanalyse</li>
                <li className="flex items-center gap-2 text-gray-600 text-sm"><Check className="w-4 h-4 text-emerald-500" />Kanban Board</li>
                <li className="flex items-center gap-2 text-gray-600 text-sm"><Check className="w-4 h-4 text-emerald-500" />Kalender-Integration</li>
                <li className="flex items-center gap-2 text-gray-400 text-sm line-through"><X className="w-4 h-4 text-gray-300" />KI-E-Mails</li>
                <li className="flex items-center gap-2 text-gray-400 text-sm line-through"><X className="w-4 h-4 text-gray-300" />Steuerberater-Export</li>
              </ul>
              <Link to={`/signup?plan=starter&billing=${isYearlyPricing ? 'yearly' : 'monthly'}`}><button className="w-full rounded-xl h-12 border-2 border-gray-300 text-gray-600 hover:border-violet-600 hover:text-violet-600 hover:bg-violet-50 font-semibold transition-colors">Jetzt starten</button></Link>
            </motion.div>

            {/* Pro - Recommended */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-3xl p-8 text-white relative shadow-2xl shadow-violet-500/30 transform md:-translate-y-4 ring-4 ring-violet-400/30">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-400 text-amber-900 rounded-full text-xs font-bold shadow-lg">EMPFOHLEN</div>
              <h3 className="text-xl font-bold mb-2">Pro</h3>
              <p className="text-white/70 text-sm mb-6">Für Selbstständige & Freelancer</p>
              <div className="mb-2">
                <span className="text-4xl font-bold">{isYearlyPricing ? '290' : '29'} €</span>
                <span className="text-white/70">/{isYearlyPricing ? 'Jahr' : 'Monat'}</span>
              </div>
              {isYearlyPricing ? (
                <p className="text-emerald-300 text-xs mb-4">Spare 58 € pro Jahr!</p>
              ) : (
                <p className="text-emerald-300 text-xs mb-4 flex items-center gap-1"><Sparkles className="w-3 h-3" />Spare über 5h pro Woche</p>
              )}
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-300" />Unbegrenzte Dokumente</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-300" />KI-Dokumentenanalyse</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-300" />KI-generierte E-Mails</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-300" />One-Click Bezahlen</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-300" />Kalender-Integration</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-300" />Steuerberater-Export</li>
              </ul>
              <Link to={`/signup?plan=pro&billing=${isYearlyPricing ? 'yearly' : 'monthly'}`}><Button className="w-full rounded-xl h-12 bg-white text-violet-700 hover:bg-white/90 font-semibold shadow-lg">Jetzt starten</Button></Link>
            </motion.div>

            {/* Business - Teams */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="bg-white rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-shadow relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-slate-800 text-white rounded-full text-xs font-bold">FÜR TEAMS</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Business</h3>
              <p className="text-gray-500 text-sm mb-6">Für Teams & Agenturen</p>
              <div className="mb-2">
                <span className="text-4xl font-bold text-gray-900">{isYearlyPricing ? '490' : '49'} €</span>
                <span className="text-gray-500">/{isYearlyPricing ? 'Jahr' : 'Monat'}</span>
              </div>
              {isYearlyPricing ? (
                <p className="text-emerald-600 text-xs mb-4">Spare 98 € pro Jahr!</p>
              ) : (
                <p className="text-violet-600 text-xs mb-4">Pro Nutzer · Ab 3 Nutzern</p>
              )}
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-gray-600 text-sm"><Check className="w-4 h-4 text-emerald-500" />Alles aus Pro</li>
                <li className="flex items-center gap-2 text-gray-600 text-sm"><Check className="w-4 h-4 text-emerald-500" />Unbegrenzte Team-Mitglieder</li>
                <li className="flex items-center gap-2 text-gray-600 text-sm"><Check className="w-4 h-4 text-emerald-500" />Multi-Company Support</li>
                <li className="flex items-center gap-2 text-gray-600 text-sm"><Check className="w-4 h-4 text-emerald-500" />Rollen & Berechtigungen</li>
                <li className="flex items-center gap-2 text-gray-600 text-sm"><Check className="w-4 h-4 text-emerald-500" />Priority Support 24/7</li>
                <li className="flex items-center gap-2 text-gray-600 text-sm"><Check className="w-4 h-4 text-emerald-500" />Dedizierter Account Manager</li>
              </ul>
              <Link to={`/signup?plan=business&billing=${isYearlyPricing ? 'yearly' : 'monthly'}`}><button className="w-full rounded-xl h-12 border-2 border-violet-600 text-violet-600 hover:bg-violet-50 font-semibold transition-colors">Jetzt starten</button></Link>
            </motion.div>
          </div>
          
          {/* Money Back & Trust - Subtle Integration */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="mt-10 text-center"
          >
            <p className="text-gray-500 text-sm flex items-center justify-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span><span className="font-semibold text-gray-700">30 Tage Geld-zurück-Garantie</span> – kein Risiko, volle Zufriedenheit</span>
            </p>
            <div className="flex items-center justify-center gap-6 text-xs text-gray-400">
              <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> Deutsche Server</span>
              <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> DSGVO-konform</span>
              <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> SSL-verschlüsselt</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== 6. KANBAN BOARD MIT ANIMATION ========== */}
      <section id="prinzip" className="py-20 bg-gradient-to-b from-white via-slate-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full text-sm font-semibold mb-4 shadow-lg shadow-violet-500/30">
              Kanban Board
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Drag & Drop System
              <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent"> für alle Dokumente</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Organisiere deine Rechnungen, Mahnungen und Briefe wie ein Profi. Verschiebe Dokumente einfach von Spalte zu Spalte.
            </p>
          </motion.div>

          {/* Full Width Kanban Board - Dark Mode Style like Real App */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-[#0f0a1e] via-[#1a1030] to-[#0d0719] rounded-2xl sm:rounded-3xl p-4 sm:p-8 border border-violet-500/30 shadow-[0_0_60px_rgba(139,92,246,0.2)] relative overflow-hidden">
              {/* Background glow */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.15),transparent_60%)]"></div>
              
              {/* Animated Card - Moves through columns */}
              <motion.div
                animate={{ 
                  left: ['5%', '52%', '52%', '5%', '5%', '52%', '5%'],
                  top: ['60px', '60px', '55%', '55%', '55%', '60px', '60px'],
                  scale: [1, 1.05, 1.1, 1.05, 1.1, 1.05, 1],
                  rotate: [0, 3, -3, 3, -3, 0, 0]
                }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 8,
                  ease: "easeInOut",
                  times: [0, 0.17, 0.33, 0.50, 0.67, 0.83, 1]
                }}
                className="absolute z-30 bg-white/10 backdrop-blur-sm rounded-xl p-2.5 sm:p-4 shadow-2xl border border-violet-500/50 w-28 sm:w-44"
                style={{ pointerEvents: 'none' }}
              >
                <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                  <div className="w-5 h-5 sm:w-7 sm:h-7 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                    <FileText className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-white" />
                  </div>
                  <span className="font-bold text-white text-[10px] sm:text-sm">Stadtwerke</span>
                </div>
                <p className="text-[9px] sm:text-xs text-orange-400 flex items-center gap-1 mb-0.5 sm:mb-1">
                  <Flame className="w-2 h-2 sm:w-3 sm:h-3" />Noch 2 Tage
                </p>
                <p className="font-bold text-white text-xs sm:text-base">127,84 €</p>
                <motion.div 
                  className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 w-4 h-4 sm:w-6 sm:h-6 bg-violet-600 rounded-full flex items-center justify-center shadow-lg"
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ repeat: Infinity, duration: 0.8 }}
                >
                  <MousePointerClick className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                </motion.div>
              </motion.div>

              {/* Column Grid - Dark Mode Style */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 relative z-10">
                {/* Pipeline */}
                <div className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-5 border border-white/10 min-h-[180px] sm:min-h-[280px]">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-white/10">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-blue-500"></div>
                    <span className="font-bold text-white text-xs sm:text-base">Pipeline</span>
                    <span className="text-[10px] sm:text-xs bg-blue-500/20 text-blue-400 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ml-auto font-semibold">5</span>
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="p-2 sm:p-3 bg-white/5 rounded-lg sm:rounded-xl border border-white/10">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center">
                          <FileText className="w-3 h-3 text-white" />
                        </div>
                        <p className="font-semibold text-white text-xs sm:text-sm">Vodafone</p>
                      </div>
                      <p className="text-[10px] sm:text-xs text-blue-400">Noch 12 Tage</p>
                      <p className="text-xs sm:text-sm text-white font-bold mt-1">39,99 €</p>
                    </div>
                    <div className="p-2 sm:p-3 bg-white/5 rounded-lg sm:rounded-xl border border-white/10">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center">
                          <FileText className="w-3 h-3 text-white" />
                        </div>
                        <p className="font-semibold text-white text-xs sm:text-sm">Amazon</p>
                      </div>
                      <p className="text-[10px] sm:text-xs text-blue-400">Noch 8 Tage</p>
                      <p className="text-xs sm:text-sm text-white font-bold mt-1">89,99 €</p>
                    </div>
                  </div>
                </div>
                
                {/* Dringend - Orange/Red highlight */}
                <div className="bg-orange-500/10 rounded-xl sm:rounded-2xl p-3 sm:p-5 border-2 border-dashed border-orange-500/50 min-h-[180px] sm:min-h-[280px]">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-orange-500/30">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-orange-500"></div>
                    <span className="font-bold text-white text-xs sm:text-base">Dringend</span>
                    <span className="text-[10px] sm:text-xs bg-orange-500/20 text-orange-400 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ml-auto font-semibold">2</span>
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="p-2 sm:p-3 bg-orange-500/10 rounded-lg sm:rounded-xl border border-orange-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                          <Flame className="w-3 h-3 text-white" />
                        </div>
                        <p className="font-semibold text-white text-xs sm:text-sm">GEZ</p>
                      </div>
                      <p className="text-[10px] sm:text-xs text-orange-400">Noch 3 Tage!</p>
                      <p className="text-xs sm:text-sm text-white font-bold mt-1">55,08 €</p>
                    </div>
                    <div className="p-2 sm:p-3 bg-white/5 rounded-lg sm:rounded-xl border-2 border-dashed border-orange-500/40 text-center py-4 sm:py-6">
                      <p className="text-[10px] sm:text-sm text-orange-400 font-medium">Hierher ziehen</p>
                    </div>
                  </div>
                </div>
                
                {/* In Bearbeitung */}
                <div className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-5 border border-violet-500/30 min-h-[180px] sm:min-h-[280px]">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-white/10">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-violet-500"></div>
                    <span className="font-bold text-white text-xs sm:text-base">In Arbeit</span>
                    <span className="text-[10px] sm:text-xs bg-violet-500/20 text-violet-400 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ml-auto font-semibold">3</span>
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="p-2 sm:p-3 bg-violet-500/10 rounded-lg sm:rounded-xl border border-violet-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-violet-500 rounded-lg flex items-center justify-center">
                          <Clock className="w-3 h-3 text-white" />
                        </div>
                        <p className="font-semibold text-white text-xs sm:text-sm">HUK</p>
                      </div>
                      <p className="text-[10px] sm:text-xs text-violet-400">Fristverlängerung</p>
                      <p className="text-xs sm:text-sm text-white font-bold mt-1">234,00 €</p>
                    </div>
                    <div className="p-2 sm:p-3 bg-violet-500/10 rounded-lg sm:rounded-xl border border-violet-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-violet-500 rounded-lg flex items-center justify-center">
                          <Mail className="w-3 h-3 text-white" />
                        </div>
                        <p className="font-semibold text-white text-xs sm:text-sm">Allianz</p>
                      </div>
                      <p className="text-[10px] sm:text-xs text-violet-400">E-Mail gesendet</p>
                    </div>
                  </div>
                </div>
                
                {/* Erledigt */}
                <div className="bg-emerald-500/10 rounded-xl sm:rounded-2xl p-3 sm:p-5 border border-emerald-500/30 min-h-[180px] sm:min-h-[280px]">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-emerald-500/30">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-emerald-500"></div>
                    <span className="font-bold text-white text-xs sm:text-base">Erledigt</span>
                    <span className="text-[10px] sm:text-xs bg-emerald-500/20 text-emerald-400 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ml-auto font-semibold">6</span>
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="p-2 sm:p-3 bg-emerald-500/10 rounded-lg sm:rounded-xl border border-emerald-500/30">
                      <p className="font-semibold text-white text-xs sm:text-sm flex items-center gap-1 sm:gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />Telekom
                      </p>
                      <p className="text-[10px] sm:text-xs text-emerald-400 mt-1">Bezahlt am 28.02.</p>
                    </div>
                    <div className="p-2 sm:p-3 bg-emerald-500/10 rounded-lg sm:rounded-xl border border-emerald-500/30">
                      <p className="font-semibold text-white text-xs sm:text-sm flex items-center gap-1 sm:gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />Miete
                      </p>
                      <p className="text-[10px] sm:text-xs text-emerald-400 mt-1">Bezahlt am 01.03.</p>
                    </div>
                    <div className="p-2 sm:p-3 bg-emerald-500/10 rounded-lg sm:rounded-xl border border-emerald-500/30 hidden sm:block">
                      <p className="font-semibold text-white text-sm flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />Strom
                      </p>
                      <p className="text-xs text-emerald-400 mt-1">Bezahlt am 25.02.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements - Hidden on Mobile */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
              className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 hidden lg:flex"
            >
              <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">Erledigt!</p>
                <p className="text-xs text-gray-500">Telekom bezahlt</p>
              </div>
            </motion.div>
            
            {/* Fristverlängerung Card */}
            <motion.div
              animate={{ y: [0, -8, 0], x: [0, 3, 0] }}
              transition={{ repeat: Infinity, duration: 3.5, delay: 0.5 }}
              className="absolute top-1/2 -right-6 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl shadow-xl p-4 flex items-center gap-3 hidden lg:flex"
            >
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-white text-sm">Frist verlängert</p>
                <p className="text-xs text-white/80">E-Mail gesendet</p>
              </div>
            </motion.div>
            
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 4 }}
              className="absolute -bottom-4 -left-4 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl shadow-xl p-4 flex items-center gap-3 hidden lg:flex"
            >
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Inbox className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-white text-sm">Neu eingegangen</p>
                <p className="text-xs text-white/70">2 Dokumente</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* ========== 7. FEATURES AUF EINEN BLICK ========== */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
              Alles in einer App
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              So funktioniert JaySmart
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white border border-gray-200 hover:shadow-xl hover:border-violet-200 transition-all group"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 6. KI-GENERIERTE E-MAILS ========== */}
      <section className="py-20 bg-gradient-to-b from-white to-violet-50 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left - Text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full text-sm font-semibold mb-4">
                KI-Assistent
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Professionelle E-Mails
                <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent"> in Sekunden</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Nie wieder Formulierungen überlegen. Unsere KI generiert professionelle E-Mails für Fristverlängerungen, Rückfragen oder Bestätigungen – perfekt auf den Kontext abgestimmt.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-violet-600" />
                  </div>
                  <span className="text-gray-700 font-medium">Ton & Stil automatisch angepasst</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                    <Mail className="w-5 h-5 text-violet-600" />
                  </div>
                  <span className="text-gray-700 font-medium">Direkt versenden oder kopieren</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-violet-600" />
                  </div>
                  <span className="text-gray-700 font-medium">Spart 10+ Minuten pro E-Mail</span>
                </div>
              </div>
            </motion.div>
            
            {/* Right - Email Preview with Float Animation */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              animate={{ y: [0, -10, 0] }}
              transition={{ 
                y: { repeat: Infinity, duration: 4, ease: "easeInOut" }
              }}
              className="w-full max-w-full overflow-hidden"
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                {/* Email Header */}
                <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 text-sm">KI-generierte E-Mail</p>
                      <p className="text-xs text-violet-600 truncate">Fristverlängerung anfragen</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        KI
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-600 truncate"><span className="text-gray-400">An:</span> buchhaltung@stadtwerke-berlin.de</p>
                    <p className="text-gray-600 truncate"><span className="text-gray-400">Betreff:</span> Bitte um Fristverlängerung – Kundennr. 847291</p>
                  </div>
                </div>
                
                {/* Email Body */}
                <div className="px-4 sm:px-6 py-4 sm:py-5">
                  <div className="prose prose-sm text-gray-700 space-y-2 sm:space-y-3 text-xs sm:text-sm">
                    <p>Sehr geehrte Damen und Herren,</p>
                    <p>
                      bezugnehmend auf Ihre Rechnung Nr. 2024-0847 vom 15.02.2026 über 127,84 € 
                      bitte ich Sie höflich um eine Verlängerung der Zahlungsfrist um 14 Tage.
                    </p>
                    <p className="hidden sm:block">
                      Aufgrund eines vorübergehenden Liquiditätsengpasses ist es mir leider nicht möglich, 
                      die Zahlung bis zum ursprünglichen Fälligkeitsdatum (28.02.2026) zu leisten.
                    </p>
                    <p className="hidden sm:block">
                      Ich versichere Ihnen, dass die Zahlung bis spätestens 14.03.2026 erfolgen wird.
                    </p>
                    <p className="hidden sm:block">
                      Für Rückfragen stehe ich Ihnen gerne zur Verfügung.
                    </p>
                    <p className="pt-2">
                      Mit freundlichen Grüßen<br />
                      <span className="font-semibold">Jay Adel</span>
                    </p>
                  </div>
                </div>
                
                {/* Email Footer Actions - Animated Send Button */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <motion.button 
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 py-2 sm:py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium text-xs sm:text-sm flex items-center justify-center gap-1 sm:gap-2 shadow-lg shadow-violet-500/25 hover:from-violet-700 hover:to-purple-700 transition-colors"
                    >
                      <Send className="w-3 h-3 sm:w-4 sm:h-4" />
                      E-Mail senden
                    </motion.button>
                    <button className="py-2 sm:py-2.5 px-2 sm:px-4 bg-white border border-gray-200 text-gray-700 rounded-xl font-medium text-xs sm:text-sm hover:bg-gray-50 transition-colors">
                      Bearbeiten
                    </button>
                  </div>
                  
                  {/* Success Message - Animated */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2, duration: 0.5 }}
                    className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2"
                  >
                    <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <p className="text-emerald-800 font-medium text-xs sm:text-sm">E-Mail erfolgreich gesendet!</p>
                      <p className="text-emerald-600 text-[10px] sm:text-xs">Fristverlängerung angefragt am 01.03.2026</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== 8. KI-FEATURES (3 Haupt-Aktionen) ========== */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-violet-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full text-sm font-semibold mb-4">
              Ein Klick. Erledigt.
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Direkt handeln – ohne Umwege
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Bezahle Rechnungen, verlängere Fristen oder stelle Rückfragen – alles mit einem Klick.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Bezahlen */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-xl border border-violet-100 text-center hover:shadow-2xl transition-shadow"
            >
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/30">
                <CreditCard className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Rechnung bezahlen</h3>
              <p className="text-gray-600 mb-6">IBAN erkannt? Ein Klick und die Überweisung ist vorbereitet.</p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                <Check className="w-4 h-4" />
                Stripe integriert
              </div>
            </motion.div>
            
            {/* Frist verlängern */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-3xl p-8 shadow-xl border border-violet-100 text-center hover:shadow-2xl transition-shadow"
            >
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Frist verlängern</h3>
              <p className="text-gray-600 mb-6">Die KI generiert eine professionelle E-Mail zur Fristverlängerung.</p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                KI-generiert
              </div>
            </motion.div>
            
            {/* Rückfrage */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-3xl p-8 shadow-xl border border-violet-100 text-center hover:shadow-2xl transition-shadow"
            >
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Rückfrage stellen</h3>
              <p className="text-gray-600 mb-6">Unklar? Die KI formuliert deine Rückfrage direkt an den Absender.</p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                <Send className="w-4 h-4" />
                Direkter Versand
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== 8. DOKUMENT-DETAIL (Tiefer platziert) ========== */}
      <section className="py-20 bg-gradient-to-br from-violet-50 via-white to-purple-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left - Text */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="inline-block px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
                KI-Dokumentenanalyse
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Jedes Detail <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">automatisch erkannt</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Lade ein Dokument hoch – unsere KI extrahiert sofort Absender, Betrag, IBAN, Fälligkeit und erstellt eine Zusammenfassung.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="text-gray-700 font-medium">Betrag & Fälligkeit extrahiert</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="text-gray-700 font-medium">IBAN & Verwendungszweck erkannt</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-gray-700 font-medium">Kunde wird automatisch angelegt</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                    <FolderCheck className="w-5 h-5 text-violet-600" />
                  </div>
                  <span className="text-gray-700 font-medium">Alle Rechnungen digital archiviert</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Download className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-gray-700 font-medium">Alles vorbereitet für den Steuerberater</span>
                </div>
              </div>
            </motion.div>
            
            {/* Right - Document Detail Modal with Float Animation */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              animate={{ y: [0, -10, 0] }}
              transition={{ 
                y: { repeat: Infinity, duration: 4, ease: "easeInOut" }
              }}
              className="w-full max-w-full overflow-hidden"
            >
              <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl p-4 sm:p-6 shadow-2xl">
                <div className="bg-gradient-to-br from-[#e8eef5] to-[#dce5ef] rounded-2xl overflow-hidden shadow-xl">
                  {/* Header */}
                  <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-violet-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 truncate">Walch Food GmbH</p>
                      <p className="text-sm text-gray-500">Rechnung</p>
                    </div>
                  </div>
                  
                  {/* Status */}
                  <div className="px-4 sm:px-6 py-2 sm:py-3 overflow-x-auto">
                    <div className="flex gap-2">
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs text-gray-500 rounded-full bg-white/50 whitespace-nowrap">Dringend</span>
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs text-white font-medium rounded-full bg-violet-500 whitespace-nowrap">Wird erledigt</span>
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs text-gray-500 rounded-full bg-white/50 whitespace-nowrap">Erledigt</span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="px-4 sm:px-6 py-2 sm:py-3">
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      <button className="flex flex-col items-center gap-1 p-2 sm:p-3 bg-violet-500 rounded-xl text-white">
                        <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="text-[10px] sm:text-xs font-medium">Bezahlen</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 p-2 sm:p-3 bg-white/50 rounded-xl text-gray-400">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="text-[10px] sm:text-xs">Frist</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 p-2 sm:p-3 bg-white/50 rounded-xl text-gray-400">
                        <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="text-[10px] sm:text-xs">Rückfrage</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Details */}
                  <div className="px-4 sm:px-6 py-2 sm:py-3">
                    <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="bg-white/60 rounded-xl p-2 sm:p-3">
                        <p className="text-[10px] sm:text-xs text-gray-500">Betrag</p>
                        <p className="text-base sm:text-lg font-bold text-gray-900">324,64 €</p>
                      </div>
                      <div className="bg-white/60 rounded-xl p-2 sm:p-3">
                        <p className="text-[10px] sm:text-xs text-gray-500">Fälligkeit</p>
                        <p className="text-base sm:text-lg font-bold text-gray-900">23.2.2026</p>
                      </div>
                    </div>
                    <div className="text-[10px] sm:text-xs space-y-1">
                      <p className="truncate"><span className="text-gray-500">IBAN:</span> <span className="font-semibold">DE63 6665 0085 ****</span></p>
                      <p className="truncate"><span className="text-gray-500">Verwendung:</span> <span className="font-semibold">Auftrag #25581</span></p>
                    </div>
                  </div>
                  
                  {/* KI Confidence */}
                  <div className="px-4 sm:px-6 py-3 sm:py-4">
                    <div className="bg-white/60 rounded-xl p-2 sm:p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] sm:text-xs text-gray-600">KI-Konfidenz</span>
                        <span className="text-[10px] sm:text-xs font-bold text-gray-900">93%</span>
                      </div>
                      <div className="w-full h-1.5 sm:h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full" style={{ width: '93%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== STEUERBERATER EXPORT - Alle Belege fertig sortiert ========== */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left - Text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-semibold mb-4 border border-emerald-500/30">
                Steuerberater-Export
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Alle Belege
                <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent"> fertig sortiert</span>
              </h2>
              <p className="text-lg text-white/70 mb-8 leading-relaxed">
                JaySmart sortiert deine Dokumente automatisch in 10 Kategorien. 
                Ein Klick – und dein Steuerberater hat alles als ZIP.
              </p>
              
              <div className="space-y-4">
                <motion.div 
                  className="flex items-center gap-3"
                  animate={{ x: [0, 3, 0] }}
                  transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                >
                  <motion.div 
                    className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center border border-emerald-500/30"
                    animate={{ scale: [1, 1.1, 1], rotate: [0, 5, 0] }}
                    transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                  >
                    <FolderCheck className="w-5 h-5 text-emerald-400" />
                  </motion.div>
                  <span className="text-white font-medium">10 vordefinierte Kategorien</span>
                </motion.div>
                <motion.div 
                  className="flex items-center gap-3"
                  animate={{ x: [0, 3, 0] }}
                  transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut", delay: 0.3 }}
                >
                  <motion.div 
                    className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center border border-emerald-500/30"
                    animate={{ scale: [1, 1.1, 1], y: [0, -3, 0] }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut", delay: 0.5 }}
                  >
                    <Download className="w-5 h-5 text-emerald-400" />
                  </motion.div>
                  <span className="text-white font-medium">Export als ZIP mit einem Klick</span>
                </motion.div>
                <motion.div 
                  className="flex items-center gap-3"
                  animate={{ x: [0, 3, 0] }}
                  transition={{ repeat: Infinity, duration: 2, ease: "easeInOut", delay: 0.6 }}
                >
                  <motion.div 
                    className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center border border-emerald-500/30"
                    animate={{ scale: [1, 1.1, 1], rotate: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut", delay: 1 }}
                  >
                    <Share2 className="w-5 h-5 text-emerald-400" />
                  </motion.div>
                  <span className="text-white font-medium">Direkt an Steuerberater senden</span>
                </motion.div>
              </div>
            </motion.div>
            
            {/* Right - Folder Grid with Animation */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="w-full max-w-full"
            >
              <div className="bg-white/5 backdrop-blur rounded-2xl p-4 sm:p-6 border border-white/10">
                {/* Folder Grid - 2x5 or 3x4 */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
                  {[
                    { name: 'Rechnungen', color: 'from-blue-500 to-blue-600', count: 47 },
                    { name: 'Behörden', color: 'from-red-500 to-red-600', count: 12 },
                    { name: 'Versicherung', color: 'from-emerald-500 to-emerald-600', count: 8 },
                    { name: 'Miete', color: 'from-amber-500 to-amber-600', count: 24 },
                    { name: 'Verträge', color: 'from-violet-500 to-violet-600', count: 15 },
                    { name: 'Gehalt', color: 'from-pink-500 to-pink-600', count: 12 },
                    { name: 'Bank', color: 'from-cyan-500 to-cyan-600', count: 36 },
                    { name: 'Gesundheit', color: 'from-rose-500 to-rose-600', count: 5 },
                    { name: 'Kfz', color: 'from-orange-500 to-orange-600', count: 9 },
                    { name: 'Sonstiges', color: 'from-slate-500 to-slate-600', count: 23 }
                  ].map((folder, i) => (
                    <motion.div
                      key={folder.name}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      whileHover={{ scale: 1.05, y: -5 }}
                      className="bg-white/5 rounded-xl p-3 sm:p-4 border border-white/10 hover:bg-white/10 transition-colors cursor-pointer group"
                    >
                      <motion.div 
                        className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br ${folder.color} rounded-lg flex items-center justify-center mb-2`}
                        animate={{ rotate: [0, 3, -3, 0] }}
                        transition={{ repeat: Infinity, duration: 4, delay: i * 0.2 }}
                      >
                        <FolderOpen className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </motion.div>
                      <p className="text-white font-semibold text-xs sm:text-sm truncate">{folder.name}</p>
                      <p className="text-white/50 text-[10px] sm:text-xs">{folder.count} Dateien</p>
                    </motion.div>
                  ))}
                </div>
                
                {/* Export Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  animate={{ boxShadow: ['0 10px 25px -5px rgba(16,185,129,0.25)', '0 10px 35px -5px rgba(16,185,129,0.4)', '0 10px 25px -5px rgba(16,185,129,0.25)'] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="mt-4 sm:mt-6 w-full py-3 sm:py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-semibold text-sm sm:text-base transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                  Alle Belege als ZIP exportieren
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== LOGO SLIDER - Empfohlen von ========== */}
      <section className="py-12 bg-gray-50 border-y border-gray-100 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-center text-sm text-gray-500 mb-8 font-medium">Empfohlen von führenden Unternehmen</p>
          <div className="relative overflow-hidden -mx-4 sm:-mx-6 px-4 sm:px-6">
            <motion.div 
              className="flex gap-16 items-center"
              animate={{ x: [0, -1200] }}
              transition={{ 
                repeat: Infinity, 
                duration: 20, 
                ease: "linear"
              }}
            >
              {/* Logo Set 1 */}
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">TechVentures</span>
              </div>
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">GreenFlow</span>
              </div>
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                  <Flame className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">FireStarter</span>
              </div>
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Star className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">NovaGroup</span>
              </div>
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">SecureBase</span>
              </div>
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">FinanzPro</span>
              </div>
              {/* Logo Set 2 (duplicate for seamless loop) */}
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">TechVentures</span>
              </div>
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">GreenFlow</span>
              </div>
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                  <Flame className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">FireStarter</span>
              </div>
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Star className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">NovaGroup</span>
              </div>
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">SecureBase</span>
              </div>
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-400">FinanzPro</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== 9. TESTIMONIALS ========== */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
              Erfahrungen
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Was unsere Nutzer sagen
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: 'Sarah M.', role: 'Freelancerin', text: 'Endlich habe ich meine Rechnungen im Griff! Die KI-Erkennung spart mir Stunden.' },
              { name: 'Thomas K.', role: 'Handwerker', text: 'Als Selbstständiger war Papierkram immer mein größtes Problem. JaySmart hat das gelöst.' },
              { name: 'Lisa W.', role: 'Beraterin', text: 'Der Steuerberater-Export ist genial. Ein Klick und alle Belege sind fertig.' }
            ].map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-gray-700 mb-6">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold">{t.name[0]}</div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== KONTAKT ========== */}
      <section id="kontakt" className="py-20 bg-gradient-to-br from-violet-600 to-purple-700">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur text-white rounded-full text-sm font-semibold mb-4">
              Kontakt
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Wir sind für dich da
            </h2>
            <p className="text-lg text-white/80 max-w-xl mx-auto">
              Hast du Fragen oder möchtest mehr erfahren? Schreib uns – wir antworten innerhalb von 24 Stunden.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center border border-white/20"
            >
              <div className="w-14 h-14 mx-auto mb-4 bg-white/20 rounded-2xl flex items-center justify-center">
                <Mail className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-white mb-2">E-Mail</h3>
              <p className="text-white/70 text-sm">support@jaysmart.de</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center border border-white/20"
            >
              <div className="w-14 h-14 mx-auto mb-4 bg-white/20 rounded-2xl flex items-center justify-center">
                <Phone className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-white mb-2">Telefon</h3>
              <p className="text-white/70 text-sm">+49 30 1234567</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center border border-white/20"
            >
              <div className="w-14 h-14 mx-auto mb-4 bg-white/20 rounded-2xl flex items-center justify-center">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-white mb-2">Standort</h3>
              <p className="text-white/70 text-sm">Berlin, Deutschland</p>
            </motion.div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-2xl"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input type="text" placeholder="Dein Name" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail</label>
                <input type="email" placeholder="deine@email.de" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-colors" />
              </div>
            </div>
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Nachricht</label>
              <textarea rows={4} placeholder="Wie können wir dir helfen?" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-colors resize-none"></textarea>
            </div>
            <button className="mt-6 w-full py-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl font-semibold text-lg shadow-lg shadow-violet-500/25 transition-colors flex items-center justify-center gap-2">
              <Send className="w-5 h-5" />
              Nachricht senden
            </button>
          </motion.div>
        </div>
      </section>

      {/* ========== 9. FAQ ========== */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Häufige Fragen</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-slate-50 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-4 text-left flex items-center justify-between">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 10. FINAL CTA ========== */}
      <section className="py-24 bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Bereit, deine Zeit zurückzubekommen?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Starte jetzt kostenlos und erlebe, wie einfach Dokumentenmanagement sein kann.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white text-violet-700 hover:bg-white/90 rounded-full px-10 h-14 text-lg font-bold shadow-2xl">
              Jetzt kostenlos starten
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <p className="mt-6 text-white/60 text-sm">Keine Kreditkarte · 14 Tage kostenlos</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Rocket className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">JaySmart</span>
              </div>
              <p className="text-gray-400 text-sm">Die smarte Lösung für dein Dokumentenmanagement.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Produkt</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Preise</a></li>
                <li><a href="#faq" className="hover:text-white">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Rechtliches</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-white">Datenschutz</a></li>
                <li><a href="#" className="hover:text-white">Impressum</a></li>
                <li><a href="#" className="hover:text-white">AGB</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Kontakt</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> info@jaysmart.de</li>
                <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> +49 30 12345678</li>
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Berlin</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
            © 2026 JaySmart. Alle Rechte vorbehalten.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
